import { PlaceV0 } from 'lib/place'

import { DataItem as CollectionResponseData } from '../api/collections'
import { Response as LookupPlaceResponse } from '../api/lookup'
import { DataItem as ScbePlaceResponseData } from '../api/places'

export interface CollectionsState {
  lastVisitedCollectionId?: string
  collectionMap: CollectionMap
  placeMap: PlaceMap
  status: Status
  error?: string
  pageToken?: string
  order: SortOrder
}

export enum SortOrder {
  dateAdded = 'dateAdded',
  name = 'name',
  distance = 'distance',
}
export type CollectionMap = Record<string, Collection>
export type PlaceMap = Record<string, Place>

export interface Collection {
  id: string
  name: string
  description: string
  category: string
  viewCount: number
}

export interface Place {
  id: string
  idV0: string
  placeId?: string
  collectionId: string[]
  imageUrl?: string
  name: string
  position: Position
  address: string
  categoryId?: string
  placeType?: string
  placesForeignId?: string
  politicalView?: string
  countryCode?: string
  tripAdvisorRating?: Rating
  createdTime: number
  updatedTime: number
}

export interface Rating {
  average: number
  count: number
  link: string
}

export interface Position {
  lat: number
  lng: number
}

export enum Status {
  createCollectionPending = 'createCollectionPending',
  createCollectionSuccess = 'createCollectionSuccess',
  createCollectionError = 'createCollectionError',
  deleteCollectionPending = 'deleteCollectionPending',
  deleteCollectionSuccess = 'deleteCollectionSuccess',
  deleteCollectionError = 'deleteCollectionError',
  editCollectionPending = 'editCollectionPending',
  editCollectionSuccess = 'editCollectionSuccess',
  editCollectionError = 'editCollectionError',
  fetchCollectionsAndPlacesPending = 'fetchCollectionsAndPlacesPending',
  fetchCollectionsAndPlacesSuccess = 'fetchCollectionsAndPlacesSuccess',
  fetchCollectionsAndPlacesError = 'fetchCollectionsAndPlacesError',
  deletePlaceInCollectionError = 'deletePlaceInCollectionError',
  deletePlaceInCollectionPending = 'deletePlaceInCollectionPending',
  deletePlaceInCollectionSuccess = 'deletePlaceInCollectionSuccess',
  savePlaceInCollectionError = 'savePlaceInCollectionError',
  savePlaceInCollectionPending = 'savePlaceInCollectionPending',
  savePlaceInCollectionSuccess = 'savePlaceInCollectionSuccess',
  unknown = 'unknown',
}

export function mapToCollection(responseData: CollectionResponseData): Collection {
  return {
    category: responseData.portraitImageUrl,
    description: responseData.description,
    id: responseData.id,
    name: responseData.name,
    viewCount: responseData.viewCount,
  }
}

export function mapToPlaceV0(
  scbeResponseData: ScbePlaceResponseData,
  intrinsicPlaceId?: string,
  lookupResponseData?: LookupPlaceResponse
): PlaceV0 {
  const title = scbeResponseData.customName ? scbeResponseData.customName : scbeResponseData.name
  const placesForeignId = scbeResponseData.placesForeignId
  const address = extractAddress(scbeResponseData, lookupResponseData)
  const categoryId = extractCategoryId(undefined, lookupResponseData)
  const placeType = extractPlaceType(lookupResponseData)
  const placeId = extractPlaceId(intrinsicPlaceId, undefined)
  const politicalView = ''

  return {
    access: {
      lat: scbeResponseData.location.position.latitude,
      lng: scbeResponseData.location.position.longitude,
    },
    address,
    categoryId: categoryId !== undefined ? categoryId : '',
    countryCode: '',
    id: placeId !== undefined ? placeId : '',
    placeType: placeType !== undefined ? placeType : '',
    placesForeignId,
    politicalView,
    position: {
      lat: scbeResponseData.location.position.latitude,
      lng: scbeResponseData.location.position.longitude,
    },
    title: title,
  }
}

export function mapToPlace(
  scbeResponseData: ScbePlaceResponseData,
  serializedPlaceV0: string,
  intrinsicPlaceId?: string,
  deserializedPlaceV0?: PlaceV0,
  lookupResponseData?: LookupPlaceResponse
): Place {
  const name = scbeResponseData.customName ? scbeResponseData.customName : scbeResponseData.name
  const address = extractAddress(scbeResponseData, lookupResponseData)
  const categoryId = extractCategoryId(deserializedPlaceV0, lookupResponseData)
  const imageUrl = extractImageUrl(lookupResponseData)
  const tripAdvisorRating = extractTripAdvisorRatings(lookupResponseData)
  const countryCode = extractCountryCode(deserializedPlaceV0)
  const placeType = extractPlaceType(lookupResponseData)
  const placeId = extractPlaceId(intrinsicPlaceId, deserializedPlaceV0)
  const placesForeignId = scbeResponseData.placesForeignId

  return {
    address,
    categoryId,
    collectionId: scbeResponseData.collectionId,
    countryCode,
    createdTime: scbeResponseData.createdTime,
    id: scbeResponseData.id,
    idV0: serializedPlaceV0,
    imageUrl,
    name,
    placeId,
    placeType,
    placesForeignId,
    politicalView: deserializedPlaceV0?.politicalView,
    position: {
      lat: scbeResponseData.location.position.latitude,
      lng: scbeResponseData.location.position.longitude,
    },
    tripAdvisorRating,
    updatedTime: scbeResponseData.updatedTime,
  }
}

function extractPlaceId(placeId?: string, deserializedPlaceV0?: PlaceV0): string | undefined {
  return placeId !== undefined
    ? placeId
    : deserializedPlaceV0 !== undefined
    ? deserializedPlaceV0.id
    : undefined
}

function extractPlaceType(lookupResponseData?: LookupPlaceResponse): string | undefined {
  return lookupResponseData !== undefined ? lookupResponseData.resultType : undefined
}

function extractCountryCode(deserializedPlaceV0?: PlaceV0): string | undefined {
  return deserializedPlaceV0 !== undefined ? deserializedPlaceV0.countryCode : undefined
}

function extractAddress(
  scbeResponseData: ScbePlaceResponseData,
  lookupResponseData?: LookupPlaceResponse
): string {
  const hasLookupAddressData =
    lookupResponseData !== undefined &&
    lookupResponseData.address !== undefined &&
    lookupResponseData.address.label !== undefined

  return hasLookupAddressData
    ? lookupResponseData.address.label
    : scbeResponseData.location.address !== undefined
    ? scbeResponseData.location.address.text
    : ''
}

function extractCategoryId(
  deserializedPlaceV0?: PlaceV0,
  lookupResponseData?: LookupPlaceResponse
): string | undefined {
  if (
    lookupResponseData !== undefined &&
    'categories' in lookupResponseData &&
    lookupResponseData.categories.length > 0
  ) {
    const primaryCategory = lookupResponseData.categories.find(
      (category) => category.primary === true
    )
    if (primaryCategory) {
      return primaryCategory.id
    }

    return lookupResponseData.categories[0].id
  }

  return deserializedPlaceV0 !== undefined ? deserializedPlaceV0.categoryId : undefined
}

function extractImageUrl(lookupResponseData?: LookupPlaceResponse): string | undefined {
  return lookupResponseData !== undefined &&
    'media' in lookupResponseData &&
    lookupResponseData.media !== undefined &&
    lookupResponseData.media.images !== undefined &&
    lookupResponseData.media.images.items.length > 0
    ? lookupResponseData.media.images.items[0].href
    : undefined
}

function extractTripAdvisorRatings(lookupResponseData?: LookupPlaceResponse): Rating | undefined {
  if (
    lookupResponseData !== undefined &&
    'media' in lookupResponseData &&
    lookupResponseData.media !== undefined &&
    lookupResponseData.media.ratings !== undefined &&
    lookupResponseData.media.ratings.items.length > 0
  ) {
    const tripAdvisorRating = lookupResponseData.media.ratings.items.find(
      (rating) => rating.supplier.id === 'tripadvisor'
    )
    if (tripAdvisorRating) {
      return {
        average: tripAdvisorRating.average,
        count: tripAdvisorRating.count,
        link: tripAdvisorRating.href,
      }
    }
  }

  return undefined
}
